<template>
  <div class="flex min-h-0 flex-1 flex-col gap-3 bg-gray-700 p-3">
    <div
      class="grid h-10 shrink-0 grid-cols-[1fr_auto_1fr] items-center gap-2 rounded-lg border bg-white px-1 shadow-2xl dark:bg-slate-800"
    >
      <div class="flex items-center gap-1">
        <slot name="toolbar-left" />
      </div>
      <h2 class="truncate text-lg font-bold">{{ title }}</h2>
      <div class="flex items-center justify-end gap-1">
        <slot
          name="toolbar-right"
          :loaded="!!blob"
          :download="download"
          :print="print"
        />
      </div>
    </div>
    <div class="flex min-h-0 flex-1 flex-row-reverse gap-3">
      <div
        v-if="$slots['option-pane']"
        class="flex w-96 shrink-0 flex-col overflow-y-auto rounded-md bg-white p-3 dark:bg-slate-800"
      >
        <slot
          name="option-pane"
          :loaded="!!blob"
          :download="download"
          :print="print"
        />
      </div>
      <div
        class="scrollbar-hidden @container/pdf min-w-0 flex-1 overflow-y-auto"
      >
        <div class="mx-auto w-full max-w-4xl @3xl/pdf:w-3/4">
          <div
            v-if="documentError"
            class="flex aspect-[1/1.414] w-full flex-col items-center gap-4 bg-slate-200 p-10 pt-20"
          >
            <FontAwesomeIcon
              :icon="['fal', 'file-pdf']"
              size="4x"
              class="text-slate-500"
            />
            <p class="text-center font-bold">
              {{ $t('global.error_generating_pdf') }}
            </p>
          </div>
          <div
            v-else-if="!blob"
            class="flex aspect-[1/1.414] w-full animate-pulse flex-col gap-3 bg-white p-10 pt-20"
          >
            <div class="mx-auto mb-8 h-4 w-72 rounded-full bg-gray-200"></div>
            <div class="h-2.5 w-48 rounded-full bg-gray-200"></div>
            <div class="h-2 max-w-[360px] rounded-full bg-gray-200"></div>
            <div class="h-2 rounded-full bg-gray-200"></div>
            <div class="h-2 max-w-[330px] rounded-full bg-gray-200"></div>
            <div class="h-2 max-w-[300px] rounded-full bg-gray-200"></div>
          </div>
          <VuePdfEmbed
            v-else
            ref="pdf"
            class="flex flex-col gap-3 transition-opacity"
            :class="{ 'opacity-50': loading }"
            :source="pdfSrc"
            @rendered="loading = false"
            @loading-failed="documentError = true"
            @rendering-failed="documentError = true"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import VuePdfEmbed from 'vue-pdf-embed'

import apiClient from '@u/apiClient'

/**
 * Funeral Manager's PdfViewer, reduced to what Crema needs: the rendered
 * PDF of `url` (reloaded when it changes), a toolbar with the title and
 * `toolbar-left` / `toolbar-right` slots, and an `option-pane` slot on the
 * right. The right slots get `download` and `print`.
 */
export default {
  name: 'PdfViewer',
  components: { FontAwesomeIcon, VuePdfEmbed },
  props: {
    /** API path of the PDF; nothing is fetched while it is null. */
    url: { type: String, required: false, default: null },
    title: { type: String, required: false, default: '' },
    downloadTitle: { type: String, required: false, default: null }
  },
  expose: ['download', 'print', 'reload'],
  data() {
    return { blob: null, loading: false, documentError: false }
  },
  computed: {
    fileName() {
      return this.downloadTitle || this.title || 'document'
    },
    pdfSrc() {
      return this.blob ? URL.createObjectURL(this.blob) : ''
    }
  },
  watch: {
    url() {
      this.reload()
    },
    pdfSrc(_value, previous) {
      if (previous) URL.revokeObjectURL(previous)
    }
  },
  mounted() {
    this.reload()
  },
  beforeUnmount() {
    if (this.pdfSrc) URL.revokeObjectURL(this.pdfSrc)
  },
  methods: {
    async reload() {
      if (!this.url) return
      this.documentError = false
      this.loading = true
      try {
        const response = await apiClient.get(this.url, {
          responseType: 'blob'
        })
        this.blob = response.data
      } catch {
        this.documentError = true
        this.loading = false
      }
    },
    download() {
      const link = document.createElement('a')
      link.href = this.pdfSrc
      link.download = `${this.fileName}.pdf`
      document.body.append(link)
      link.click()
      link.remove()
    },
    print() {
      this.$refs.pdf?.print(300, `${this.fileName}.pdf`, true)
    }
  }
}
</script>
